import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getStudents, searchCachedContacts } from './api'
import { keys } from './queryKeys'
import { normalizePhone, prettyPhone } from './phone'
import type { Student } from '../types'

// Resolve a bare phone number to a human name, looking it up against students
// first and then the cached Google contacts (#21). Falls back to a prettified
// number when nothing matches. Returns a stable resolver function.
export function useContactName() {
  const { data: students } = useQuery<Student[]>({
    queryKey: keys.students,
    queryFn:  getStudents,
  })

  // The empty query returns the whole cache (same endpoint Messages search uses).
  const { data: contacts } = useQuery({
    queryKey: ['contacts-cache', 'all'],
    queryFn:  () => searchCachedContacts(''),
    staleTime: 5 * 60_000,
    retry: false,
  })

  const byNumber = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of contacts ?? []) {
      if (c.phone) map.set(normalizePhone(c.phone), c.name)
    }
    // Students win over Google contacts on a collision.
    for (const s of students ?? []) {
      if (s.whatsappPhone) map.set(normalizePhone(s.whatsappPhone), s.name)
    }
    return map
  }, [students, contacts])

  return (phone: string | null | undefined): string => {
    if (!phone) return '—'
    return byNumber.get(normalizePhone(phone)) ?? prettyPhone(phone)
  }
}
